import React from "react";
import { Tag, Tooltip } from "antd";
import { TRANSACTION_STATUS_COLORS, TRANSACTION_STATUS_LABELS, TRANSACTION_STATUS_ICONS, TRANSACTION_TYPE_LABELS } from "../constants";
import "./TransactionStyles.css";

/**
 * 事务状态标签组件
 * 用于显示事务的当前状态
 *
 * @param {Object} props
 * @param {string} props.status - 事务状态
 * @param {string} props.type - 事务类型
 * @param {boolean} props.showIcon - 是否显示图标
 * @param {string} props.size - 标签大小 'small' | 'default'
 * @param {boolean} props.textOnly - 是否仅显示文字，无边框和背景
 * @param {Object} props.style - 额外样式
 */
const TransactionStatusTag = ({ status, type, showIcon = true, size = "small", textOnly = true, style = {} }) => {
    // 允许显示空状态
    const displayEmptyState = status === null || status === undefined;

    // 如果状态为空且允许空状态显示，则使用简单的"-"样式
    const color = displayEmptyState ? "inherit" : TRANSACTION_STATUS_COLORS[status] || TRANSACTION_STATUS_COLORS.default;
    const label = displayEmptyState ? "-" : TRANSACTION_STATUS_LABELS[status] || status;
    const icon = showIcon ? (displayEmptyState ? null : TRANSACTION_STATUS_ICONS[status] || TRANSACTION_STATUS_ICONS.default) : null;
    const typeLabel = type ? TRANSACTION_TYPE_LABELS[type] : "";

    // 标签尺寸样式 - 使用开发中样式的风格
    const sizeStyles = {
        small: {
            fontSize: "11px",
            padding: "0 4px",
            height: "22px",
            lineHeight: "20px",
            margin: 0,
            borderRadius: "3px",
            fontWeight: 400,
            opacity: 0.85,
            background: `rgba(${getColorRgb(color)}, 0.1)`,
            color: color,
            border: `1px solid rgba(${getColorRgb(color)}, 0.2)`,
        },
        default: {
            fontSize: "12px",
            padding: "0 8px",
            height: "22px",
            lineHeight: "20px",
            borderRadius: "4px",
            fontWeight: 400,
            opacity: 0.85,
            background: `rgba(${getColorRgb(color)}, 0.1)`,
            color: color,
            border: `1px solid rgba(${getColorRgb(color)}, 0.2)`,
        },
    };

    const tooltipTitle = typeLabel ? `${typeLabel} - ${label}` : label;

    // 纯文本模式，只显示带颜色的文字，无背景和边框
    if (textOnly) {
        const textOnlyStyles = {
            fontSize: size === "small" ? "11px" : "13px",
            fontWeight: size === "small" ? 500 : 700,
            color: color,
            margin: 0,
            padding: 0,
            display: "inline-flex",
            alignItems: "center",
        };

        return (
            <span className="transaction-status-text" style={{ ...textOnlyStyles, ...style }}>
                {showIcon && (
                    <span className="status-icon" style={{ marginRight: "4px" }}>
                        {icon}
                    </span>
                )}
                {label}
            </span>
        );
    }

    return (
        <Tag
            style={{
                ...sizeStyles[size],
                ...style,
            }}
            className="transaction-status-tag"
            icon={showIcon ? icon : null}
        >
            {label}
        </Tag>
    );
};

// 将颜色转换为RGB格式用于设置透明度
function getColorRgb(color) {
    // 检查是否为十六进制颜色值
    if (color && color.startsWith("#")) {
        // 解析十六进制颜色
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return `${r}, ${g}, ${b}`;
    }

    // 使用预定义颜色映射
    const colorMap = {
        green: "82, 196, 26",
        red: "255, 77, 79",
        blue: "24, 144, 255",
        orange: "250, 173, 20",
        purple: "114, 46, 209",
        cyan: "19, 194, 194",
        gray: "140, 140, 140",
        "#52c41a": "82, 196, 26", // 绿色
        "#f5222d": "255, 77, 79", // 红色
        "#1890ff": "24, 144, 255", // 蓝色
        "#faad14": "250, 173, 20", // 橙色
        "#ffb84d": "255, 184, 77", // 橙黄色
        "#d9d9d9": "217, 217, 217", // 灰色
        default: "140, 140, 140",
    };

    return colorMap[color] || colorMap.default;
}

export default TransactionStatusTag;
